import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import {
  FaUniversity,
  FaFileAlt,
  FaHistory,
  FaClock,
  FaBook,
  FaCheckCircle,
  FaTools,
  FaPercentage,
  FaTimesCircle,
  FaSignOutAlt,
  FaSearch,
  FaCog,
  FaImage,
  FaEye,
  FaVolumeUp,
  FaVolumeMute,
} from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { toast } from "react-toastify";
import { useAdmin } from "./admin-context/AdminContext";
import AdminList from "./AdminList";
import { db } from "../firebase/firebaseConfig";
import PriceModal from "./modals/PriceModal";
import AdminOrderHistoryModal from "./AdminOrderHistory";
import ScheduleModal from "./modals/ScheduleModal";
import BindingModal from "./modals/BindingModal";
import PrintConfirmationModal from "./modals/PrintConfirmationModal";
import PrintedModal from "./modals/PrintedModal";
import DiscountModal from "./modals/DiscountModal";
import ImageCostModal from "./modals/ImageCostModal";
import FileViewerModal from "./modals/FileViewerModal";
import ViewFilesModal from "./modals/ViewFilesModal";
import "./adminStyle.css";

const colleges = [
  { name: "MRU", icon: <FaUniversity className="text-indigo-500" /> },
  { name: "MRIIRS", icon: <FaUniversity className="text-green-500" /> },
  { name: "Echelon College", icon: <FaUniversity className="text-red-500" /> },
];

const AdminDashboard = () => {
  const [expandedFileIndex, setExpandedFileIndex] = useState(null);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [expandedGroupIndex, setExpandedGroupIndex] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isImageCostModalOpen, setIsImageCostModalOpen] = useState(false);
  const [isBindingModalOpen, setIsBindingModalOpen] = useState(false);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isPrintConfirmationModalOpen, setIsPrintConfirmationModalOpen] = useState(false);
  const [isPrintedModalOpen, setIsPrintedModalOpen] = useState(false);
  const [isFileViewerModalOpen, setIsFileViewerModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [priceType, setPriceType] = useState("pdf");
  const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [prices, setPrices] = useState({
    pdf: null,
    ppt: {
      colorType: {
        "Black & White": { 1: 3, 2: 6, 4: 8 },
        Color: { fixedPrice: 15 },
      },
      paperSize: { A4: 0, A3: 2 },
      paperType: {
        "70GSM Paper": 5,
        "100GSM Paper": 7,
        "Bond Paper": 10,
        "Cartridge Paper": 12,
        "Glossy Paper": 15,
      },
    },
    jpg: null,
  });
  const [updatedPrices, setUpdatedPrices] = useState({
    pdf: null,
    ppt: {
      colorType: {
        "Black & White": { 1: 5, 2: 6, 4: 8 },
        Color: { fixedPrice: 15 },
      },
      paperSize: { A4: 0, A3: 2 },
      paperType: {
        "70GSM Paper": 5,
        "100GSM Paper": 7,
        "Bond Paper": 10,
        "Cartridge Paper": 12,
        "Glossy Paper": 15,
      },
    },
    jpg: null,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageCostSettings, setImageCostSettings] = useState({
    blackAndWhite: { A4: 2, A3: 5 },
    color: { A4: 5, A3: 10 },
  });
  const [selectedCollege, setSelectedCollege] = useState("");
  const [uploadStatus, setUploadStatus] = useState({
    enabled: true,
    manualOverride: false,
  });
  const [urgentFilter, setUrgentFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentOrderFiles, setCurrentOrderFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [orderIdToPrint, setOrderIdToPrint] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploadEnabled, setIsUploadEnabled] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [bindingPrices, setBindingPrices] = useState({
    minPages: 30,
    fixedPrice: 30,
    perPagePrice: 0.5,
  });
  const [discountSettings, setDiscountSettings] = useState({
    discount10to50: 10,
    discountAbove50: 20,
  });
  const [scheduleSettings, setScheduleSettings] = useState({
    isSchedulingEnabled: false,
    days: {
      monday: { enabled: false, start: "08:00", end: "16:00" },
      tuesday: { enabled: false, start: "08:00", end: "16:00" },
      wednesday: { enabled: false, start: "08:00", end: "16:00" },
      thursday: { enabled: false, start: "08:00", end: "16:00" },
      friday: { enabled: false, start: "08:00", end: "16:00" },
      saturday: { enabled: false, start: "08:00", end: "16:00" },
      sunday: { enabled: false, start: "08:00", end: "16:00" },
    },
    exceptions: [],
  });

  // Sound notification state
  const [prevOrdersCount, setPrevOrdersCount] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const isPageVisibleRef = useRef(true);

  const { isAdmin, isLoading, setIsAdmin } = useAdmin();
  const navigate = useNavigate();

  // Initialize audio context and load sound
  useEffect(() => {
    
    // Initialize audio context
    const initAudio = async () => {
      try {
        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();

        // Load audio file
        const response = await fetch("/notification.mp3");
        const arrayBuffer = await response.arrayBuffer();
        audioBufferRef.current = await audioContextRef.current.decodeAudioData(arrayBuffer);
      } catch (error) {
        console.error("Audio initialization failed:", error);
      }
    };

    initAudio();

    // Request notification permission
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        console.log("Notification permission:", permission);
      });
    }

    return () => {
      // Cleanup
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play sound when new orders arrive and page is visible
  useEffect(() => {
    if (orders.length <= prevOrdersCount || isMuted) {
      setPrevOrdersCount(orders.length);
      return;
    }

    // Play sound
    const playSound = () => {
      if (!audioContextRef.current || !audioBufferRef.current) return;

      try {
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBufferRef.current;
        source.connect(audioContextRef.current.destination);

        // Resume audio context if suspended (required by some browsers)
        if (audioContextRef.current.state === "suspended") {
          audioContextRef.current.resume();
        }

        source.start(0);
      } catch (error) {
        console.error("Error playing sound:", error);
      }
    };

    playSound();

    // Show desktop notification if available
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("New Order Received", {
          body: `New order #${orders[0].orderId} has arrived`,
          icon: "/logo.png",
          requireInteraction: false,
        });
      } catch (e) {
        console.log("Notification error:", e);
      }
    }

    setPrevOrdersCount(orders.length);
  }, [orders, prevOrdersCount, isMuted]);

  const toggleGroupDetails = (index) => {
    setExpandedGroupIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const toggleMaintenanceMode = async () => {
    try {
      const docRef = doc(db, "settings", "maintenanceMode");
      await setDoc(docRef, { enabled: !isMaintenanceMode });
      setIsMaintenanceMode(!isMaintenanceMode);
      toast.success(`Maintenance mode is now ${!isMaintenanceMode ? "ON" : "OFF"}`);
    } catch (error) {
      console.error("Error updating maintenance mode:", error);
      toast.error("Failed to update maintenance mode!");
    }
  };

  const fetchMaintenanceMode = async () => {
    try {
      const docRef = doc(db, "settings", "maintenanceMode");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setIsMaintenanceMode(docSnap.data().enabled);
      }
    } catch (error) {
      console.error("Error fetching maintenance mode:", error);
    }
  };

  const fetchBindingPrices = async () => {
    try {
      const docRef = doc(db, "settings", "bindingPrices");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setBindingPrices(docSnap.data());
      } else {
        const defaultPrices = {
          minPages: 30,
          fixedPrice: 30,
          perPagePrice: 0.5,
        };
        await setDoc(docRef, defaultPrices);
        setBindingPrices(defaultPrices);
      }
    } catch (error) {
      console.error("Error fetching binding prices:", error);
    }
  };

  const updateBindingPrices = async () => {
    try {
      await setDoc(doc(db, "settings", "bindingPrices"), bindingPrices);
      toast.success("Binding prices updated successfully!");
    } catch (error) {
      console.error("Error updating binding prices:", error);
      toast.error("Failed to update binding prices!");
    }
  };

  const fetchImageCost = async () => {
    try {
      const docRef = doc(db, "settings", "imagePageCost");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setImageCostSettings({
          blackAndWhite: {
            A4: data.blackAndWhite?.A4 || 2,
            A3: data.blackAndWhite?.A3 || 5,
          },
          color: {
            A4: data.color?.A4 || 5,
            A3: data.color?.A3 || 10,
          },
        });
      } else {
        const defaultCosts = {
          blackAndWhite: { A4: 2, A3: 5 },
          color: { A4: 5, A3: 10 },
        };
        await setDoc(docRef, defaultCosts);
        setImageCostSettings(defaultCosts);
      }
    } catch (error) {
      console.error("Error fetching image cost:", error);
      setImageCostSettings({
        blackAndWhite: { A4: 2, A3: 5 },
        color: { A4: 5, A3: 10 },
      });
    }
  };

  const handleUpdateImageCost = async () => {
    try {
      await setDoc(doc(db, "settings", "imagePageCost"), {
        ...imageCostSettings,
        updatedAt: serverTimestamp(),
      });
      toast.success("Image/Page costs updated successfully!");
    } catch (error) {
      console.error("Error updating image costs:", error);
      toast.error("Failed to update image costs");
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}m ${s < 10 ? "0" : ""}${s}s`;
  };

  const fetchUploadStatus = async () => {
    try {
      const docRef = doc(db, "settings", "uploadStatus");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUploadStatus({
          enabled: docSnap.data().enabled,
          manualOverride: docSnap.data().manualOverride || false,
        });
        setIsUploadEnabled(docSnap.data().enabled);
      } else {
        await setDoc(docRef, { enabled: true, manualOverride: false });
        setUploadStatus({ enabled: true, manualOverride: false });
        setIsUploadEnabled(true);
      }
    } catch (error) {
      console.error("Error fetching upload status:", error);
      toast.error("Failed to fetch upload status!");
    }
  };

  const toggleUploadStatus = async () => {
    try {
      const newStatus = !uploadStatus.enabled;
      const docRef = doc(db, "settings", "uploadStatus");
      await setDoc(docRef, {
        enabled: newStatus,
        manualOverride: true,
        manualOverrideTimestamp: serverTimestamp(),
      });
      setUploadStatus({
        enabled: newStatus,
        manualOverride: true,
      });
      setIsUploadEnabled(newStatus);
      toast.success(`File uploads are now ${newStatus ? "enabled" : "disabled"} (Manual override)`);
    } catch (error) {
      console.error("Error updating upload status:", error);
      toast.error("Failed to update upload status!");
    }
  };

  const fetchPrices = async () => {
    try {
      const pdfDocRef = doc(db, "settings", "pdfCombinationPrices");
      const pptDocRef = doc(db, "settings", "pptCombinationPrices");
      const jpgDocRef = doc(db, "settings", "jpgCombinationPrices");

      const [pdfDocSnap, pptDocSnap, jpgDocSnap] = await Promise.all([
        getDoc(pdfDocRef),
        getDoc(pptDocRef),
        getDoc(jpgDocRef),
      ]);

      const defaultPrices = {
        "Black & White": {},
        Color: {},
        currentColorType: "Black & White",
      };

      setPrices({
        pdf: pdfDocSnap.exists()
          ? { ...pdfDocSnap.data(), currentColorType: "Black & White" }
          : defaultPrices,
        ppt: pptDocSnap.exists()
          ? { ...pptDocSnap.data(), currentColorType: "Black & White" }
          : defaultPrices,
        jpg: jpgDocSnap.exists()
          ? { ...jpgDocSnap.data(), currentColorType: "Black & White" }
          : defaultPrices,
      });

      setUpdatedPrices({
        pdf: pdfDocSnap.exists()
          ? { ...pdfDocSnap.data(), currentColorType: "Black & White" }
          : defaultPrices,
        ppt: pptDocSnap.exists()
          ? { ...pptDocSnap.data(), currentColorType: "Black & White" }
          : defaultPrices,
        jpg: jpgDocSnap.exists()
          ? { ...jpgDocSnap.data(), currentColorType: "Black & White" }
          : defaultPrices,
      });
    } catch (error) {
      console.error("Error fetching prices:", error);
      toast.error("Failed to fetch pricing data!");
    }
  };

  const updatePrices = async () => {
    try {
      const docRef = doc(
        db,
        "settings",
        priceType === "pdf"
          ? "pdfCombinationPrices"
          : priceType === "ppt"
          ? "pptCombinationPrices"
          : "jpgCombinationPrices"
      );

      const pricesToSave = {
        "Black & White": updatedPrices[priceType]["Black & White"],
        Color: updatedPrices[priceType]["Color"],
      };

      await setDoc(docRef, pricesToSave);
      toast.success(`${priceType.toUpperCase()} prices updated successfully!`);
      fetchPrices();
    } catch (error) {
      console.error("Error updating prices:", error);
      toast.error("Failed to update pricing!");
    }
  };

  const fetchScheduleSettings = async () => {
    try {
      const docRef = doc(db, "settings", "uploadSchedule");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setScheduleSettings(docSnap.data());
      } else {
        await setDoc(docRef, scheduleSettings);
      }
    } catch (error) {
      console.error("Error fetching schedule settings:", error);
    }
  };

  const saveScheduleSettings = async () => {
    try {
      await setDoc(doc(db, "settings", "uploadSchedule"), scheduleSettings);
      toast.success("Schedule settings updated successfully!");
    } catch (error) {
      console.error("Error updating schedule settings:", error);
      toast.error("Failed to update schedule settings!");
    }
  };

  const handlePriceChange = (category, key, value) => {
    setUpdatedPrices((prev) => ({
      ...prev,
      [priceType]: {
        ...prev[priceType],
        [category]: {
          ...prev[priceType][category],
          [key]: Number(value),
        },
      },
    }));
  };

  const fetchOrders = () => {
    try {
      setLoading(true);
      const orderHistoryRef = collection(db, "finalOrder");
      const unsubscribe = onSnapshot(orderHistoryRef, (querySnapshot) => {
        const ordersData = [];
        querySnapshot.forEach((doc) => {
          let orderData = doc.data();
          if (orderData.date?.seconds) {
            const rawDate = new Date(orderData.date.seconds * 1000);
            orderData.rawDate = rawDate;
            orderData.date = rawDate.toLocaleDateString();
          }
          ordersData.push(orderData);
        });
        setOrders(ordersData);
        setLoading(false);
      });
      return unsubscribe;
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders!");
      setLoading(false);
    }
  };

  const handleViewFiles = (order) => {
    setCurrentOrderFiles(order.items);
    setCurrentOrderId(order.orderId);
    setIsModalOpen(true);
  };

  const toggleFileDetails = (index) => {
    setExpandedFileIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setIsFileViewerModalOpen(true);
  };

  const getFileViewerURL = (file) => {
    const extension = file.fileName.split(".").pop().toLowerCase();
    const supportedFormats = ["pdf", "jpg", "jpeg", "png", "gif"];
    return supportedFormats.includes(extension)
      ? file.fileURL
      : `https://docs.google.com/viewer?url=${encodeURIComponent(file.fileURL)}&embedded=true`;
  };

  const handlePrint = (file) => {
    const fileURL = file.fileURL;
    const printWindow = window.open(fileURL, "_blank");
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  const handlePrintClick = (orderId) => {
    setOrderIdToPrint(orderId);
    setIsPrintConfirmationModalOpen(true);
  };

  const handlePrintedStatusClick = (orderId) => {
    setSelectedOrderId(orderId);
    setIsPrintedModalOpen(true);
  };

  const confirmPrint = async () => {
    setIsProcessing(true);
    if (orderIdToPrint) {
      try {
        const orderQuery = query(
          collection(db, "finalOrder"),
          where("orderId", "==", orderIdToPrint)
        );
        const querySnapshot = await getDocs(orderQuery);
        if (querySnapshot.empty) {
          toast.error("Order not found!");
          setIsProcessing(false);
          return;
        }
        const orderDoc = querySnapshot.docs[0];
        const orderRef = doc(db, "finalOrder", orderDoc.id);
        await updateDoc(orderRef, { printed: true, notified: true });
        const orderData = orderDoc.data();
        const { userId } = orderData;

        if (!userId) {
          console.warn("⚠️ User ID missing for this order.");
          setIsProcessing(false);
          return;
        }

        const userDoc = await getDoc(doc(db, "users", userId));
        const userEmail = userDoc.exists() ? userDoc.data().email : null;

        if (!userEmail) {
          console.warn("⚠️ No email found for this user.");
          toast.warn("No email found for this user. Email not sent.");
          setIsProcessing(false);
          return;
        }

        const response = await fetch("https://sendorderprintemail-qwfnnmalla-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userEmail, orderId: orderIdToPrint }),
        });
        const data = await response.json();
        console.log("📧 Email Send Response:", data);

        if (data.success) {
          toast.success("Order marked as printed and email sent!");
        } else {
          toast.error("Failed to send email notification.");
        }
      } catch (error) {
        console.error("Error marking order as printed:", error);
        toast.error("Failed to update order.");
      } finally {
        setIsProcessing(false);
      }
    }
    setOrderIdToPrint(null);
    setIsPrintConfirmationModalOpen(false);
  };

  const closePrintedModal = async (status) => {
    if (status === "Yes" && selectedOrderId) {
      setIsProcessing(true);
      try {
        const storage = getStorage();
        const order = orders.find((order) => order.orderId === selectedOrderId);
        if (!order) {
          toast.error("Order not found!");
          setIsProcessing(false);
          return;
        }

        const orderIdString = String(order.orderId);

        // Save to orderCheck collection
        const orderCheckDocRef = doc(db, "orderHistory", orderIdString);
        await setDoc(orderCheckDocRef, {
          orderId: order.orderId,
          totalAmount: order.totalAmount,
          date: order.date,
        });

        const filesToDelete = order.items.map((item) => item.fileURL);
        await Promise.allSettled(
          filesToDelete.map(async (fileURL) => {
            if (fileURL) {
              const fileRef = ref(storage, fileURL);
              await deleteObject(fileRef);
            }
          })
        );
        const orderQuery = query(
          collection(db, "finalOrder"),
          where("orderId", "==", selectedOrderId)
        );
        const querySnapshot = await getDocs(orderQuery);
        if (querySnapshot.empty) {
          toast.error("No matching order found in Firestore.");
          setIsProcessing(false);
          return;
        }
        const orderDoc = querySnapshot.docs[0];
        const orderRef = doc(db, "finalOrder", orderDoc.id);
        await deleteDoc(orderRef);
        setOrders((prev) => prev.filter((order) => order.orderId !== selectedOrderId));
        toast.success("Order and associated files deleted successfully.");
      } catch (error) {
        console.error("Error deleting order and files:", error);
        toast.error("Failed to delete order and files.");
      } finally {
        setIsProcessing(false);
      }
    }
    setIsPrintedModalOpen(false);
  };

  const auth = getAuth();
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      localStorage.removeItem("isAdmin");
      navigate("/admin-dashboard-login");
      toast.success("Logged out successfully!");
    } catch (error) {
      console.error("Error logging out:", error);
      toast.error("Failed to log out.");
    }
  };

  const fetchDiscountSettings = async () => {
    try {
      const docRef = doc(db, "settings", "discountRates");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setDiscountSettings(docSnap.data());
      } else {
        const defaultDiscounts = { discount10to50: 10, discountAbove50: 20 };
        await setDoc(docRef, defaultDiscounts);
        setDiscountSettings(defaultDiscounts);
      }
    } catch (error) {
      console.error("Error fetching discount settings:", error);
      toast.error("Failed to fetch discount settings!");
    }
  };

  const updateDiscountSettings = async () => {
    try {
      const docRef = doc(db, "settings", "discountRates");
      await setDoc(docRef, discountSettings);
      toast.success("Discount settings updated successfully!");
      fetchDiscountSettings();
    } catch (error) {
      console.error("Error updating discount settings:", error);
      toast.error("Failed to update discount settings!");
    }
  };

  const handleDiscountChange = (key, value) => {
    setDiscountSettings((prev) => ({
      ...prev,
      [key]: Number(value),
    }));
  };

  const handleColorTypeChange = (colorType) => {
    setUpdatedPrices((prev) => ({
      ...prev,
      [priceType]: {
        ...prev[priceType],
        currentColorType: colorType,
      },
    }));
  };

  const handleCombinationPriceChange = (paperType, paperSize, pagesPerSheet, value) => {
    const comboKey = `${paperType}_${paperSize}_${pagesPerSheet}`;
    setUpdatedPrices((prev) => ({
      ...prev,
      [priceType]: {
        ...prev[priceType],
        [prev[priceType].currentColorType]: {
          ...prev[priceType][prev[priceType].currentColorType],
          [comboKey]: Number(value),
        },
      },
    }));
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  useEffect(() => {
    fetchMaintenanceMode();
    fetchBindingPrices();
    fetchImageCost();
    fetchUploadStatus();
    fetchScheduleSettings();
    fetchPrices();
    fetchDiscountSettings();
    const unsubscribe = fetchOrders();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "settings", "uploadStatus"), (doc) => {
      if (doc.exists()) {
        const newStatus = doc.data();
        setUploadStatus({
          enabled: newStatus.enabled,
          manualOverride: newStatus.manualOverride || false,
        });
        setIsUploadEnabled(newStatus.enabled);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      navigate("/admin-dashboard-login");
    }
  }, [isAdmin, isLoading, navigate]);

  const filteredByCollege = selectedCollege
    ? orders.filter((order) => order.items.some((item) => item.selectedCollege === selectedCollege))
    : orders;
  const filteredByUrgent =
    urgentFilter === "All"
      ? filteredByCollege
      : filteredByCollege.filter((order) =>
          urgentFilter === "Urgent" ? order.isUrgent : !order.isUrgent
        );
  const searchedOrders = searchTerm
    ? filteredByUrgent.filter((order) => {
        const orderId = order.orderId ? String(order.orderId).toLowerCase().trim() : "";
        const searchText = searchTerm.toLowerCase().trim();
        return orderId.includes(searchText);
      })
    : filteredByUrgent;

  // Separate orders into new and notified
  const newOrders = searchedOrders.filter((order) => !order.notified);
  const notifiedOrders = searchedOrders.filter((order) => order.notified);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="w-64 bg-slate-300 shadow-lg p-4 admin-sidebar">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Admin Panel</h2>
        <div className="flex items-center gap-2 mb-4">
          <span>Upload Status:</span>
          {uploadStatus.enabled ? (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full flex items-center">
              Enabled
            </span>
          ) : (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-sm rounded-full flex items-center">
              Disabled
              {uploadStatus.manualOverride ? (
                <span className="ml-1 text-xs">(Manual - expires in 25min)</span>
              ) : (
                <span className="ml-1 text-xs">(Scheduled)</span>
              )}
            </span>
          )}
        </div>
        {uploadStatus.manualOverride && (
          <button
            onClick={async () => {
              await setDoc(doc(db, "settings", "uploadStatus"), {
                manualOverride: false,
                manualOverrideTimestamp: null,
              });
              setUploadStatus((prev) => ({ ...prev, manualOverride: false }));
            }}
            className="w-full mb-2 flex items-center space-x-2 p-2 bg-yellow-900 text-white rounded-md hover:bg-yellow-800"
          >
            Revert to Auto
          </button>
        )}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Uploads</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isUploadEnabled}
                onChange={toggleUploadStatus}
                className="sr-only"
              />
              <div
                className={`w-11 h-6 bg-gray-200 rounded-full transition-colors duration-200 ${
                  isUploadEnabled ? "bg-green-500" : "bg-red-500"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-200 ${
                    isUploadEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </div>
            </label>
          </div>
          <button
            onClick={() => setIsScheduleModalOpen(true)}
            className="w-full flex items-center space-x-2 p-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600"
          >
            <FaClock className="text-lg" />
            <span>Upload Schedule</span>
          </button>
          <button
            onClick={toggleMaintenanceMode}
            className={`w-full flex items-center space-x-2 p-2 rounded-md ${
              isMaintenanceMode
                ? "bg-red-500 hover:bg-red-600"
                : "bg-yellow-500 hover:bg-yellow-600"
            } text-white`}
          >
            <FaTools className="text-lg" />
            <span>Maintenance Mode: {isMaintenanceMode ? "ON" : "OFF"}</span>
          </button>
          <button
            onClick={toggleMute}
            className={`w-full flex items-center space-x-2 p-2 ${
              isMuted ? "bg-gray-500" : "bg-green-500"
            } text-white rounded-md hover:bg-green-600`}
          >
            {isMuted ? <FaVolumeMute className="text-lg" /> : <FaVolumeUp className="text-lg" />}
            <span>{isMuted ? "Unmute Notifications" : "Mute Notifications"}</span>
          </button>
          <div>
            <label htmlFor="college-select" className="text-gray-700 mb-1 block">
              Select College
            </label>
            <select
              id="college-select"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedCollege}
              onChange={(e) => setSelectedCollege(e.target.value)}
            >
              <option value="">All Colleges</option>
              {colleges.map((college, index) => (
                <option key={index} value={college.name}>
                  {college.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="urgent-filter" className="text-gray-700 mb-1 block">
              Urgent Filter
            </label>
            <select
              id="urgent-filter"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={urgentFilter}
              onChange={(e) => setUrgentFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Urgent">Urgent</option>
              <option value="Non-urgent">Non-urgent</option>
            </select>
          </div>
          <div>
            <label htmlFor="search-order" className="text-gray-700 mb-1 block">
              Search Order by ID
            </label>
            <div className="relative">
              <input
                id="search-order"
                type="text"
                placeholder="Enter Order ID"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute right-3 top-3 text-gray-400" />
            </div>
          </div>
          <button
            onClick={() => {
              setPriceType("pdf");
              setIsPriceModalOpen(true);
            }}
            className="w-full flex items-center space-x-2 p-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            <FaCog className="text-lg" />
            <span>Update PDF Prices</span>
          </button>
          <button
            onClick={() => {
              setPriceType("ppt");
              setIsPriceModalOpen(true);
            }}
            className="w-full flex items-center space-x-2 p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <FaCog className="text-lg" />
            <span>Update PPT Prices</span>
          </button>
          <button
            onClick={() => {
              setPriceType("jpg");
              setIsPriceModalOpen(true);
            }}
            className="w-full flex items-center space-x-2 p-2 bg-teal-500 text-white rounded-md hover:bg-teal-600"
          >
            <FaCog className="text-lg" />
            <span>Update JPG Prices</span>
          </button>
          <button
            onClick={() => setIsImageCostModalOpen(true)}
            className="w-full flex items-center space-x-2 p-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
          >
            <FaImage className="text-lg" />
            <span>Image/Page Cost</span>
          </button>
          <button
            onClick={() => setIsBindingModalOpen(true)}
            className="w-full flex items-center space-x-2 p-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
          >
            <FaBook className="text-lg" />
            <span>Update Binding Prices</span>
          </button>
          <button
            onClick={() => setIsDiscountModalOpen(true)}
            className="w-full flex items-center space-x-2 p-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            <FaPercentage className="text-lg" />
            <span>Update Discounts</span>
          </button>
          <AdminList />
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-2 p-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            <FaSignOutAlt className="text-lg" />
            <span>Logout</span>
          </button>
          <button
            onClick={() => setIsOrderHistoryOpen(true)}
            className="w-full flex items-center space-x-2 p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <FaHistory className="text-lg" />
            <span>Order History</span>
          </button>
        </div>
      </div>

      <div className="flex-1 p-6 admin-main-content">
        <h2 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h2>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <ClipLoader color="#4B5563" size={40} />
          </div>
        ) : (
          <div className="space-y-6">
            {searchedOrders.length === 0 ? (
              <p className="text-center text-gray-500 text-lg">
                No orders found for the selected college or search term.
              </p>
            ) : (
              <div>
                {/* New Orders Section */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">New Orders</h3>
                  {newOrders.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg">No new orders available.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {newOrders.map((order, index) => {
                        const elapsedSeconds = order.rawDate
                          ? (currentTime - order.rawDate) / 1000
                          : 0;
                        const remainingSeconds = order.rawDate
                          ? Math.max(180 - elapsedSeconds, 0)
                          : 0;
                        const isRestricted = !order.isUrgent && remainingSeconds > 0;
                        const formattedTime = formatTime(remainingSeconds);

                        return (
                          <div
                            key={index}
                            className="relative bg-white w-[fit-content] rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200 ease-in-out overflow-hidden"
                          >
                            {isRestricted && (
                              <div className="absolute inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex flex-col justify-center items-center text-center text-red-600 font-semibold text-sm z-10">
                                <p className="mb-2">
                                  Access restricted for{" "}
                                  <span className="font-bold">{formattedTime}</span>
                                </p>
                                <p className="text-xs text-gray-600">
                                  Please wait until the time expires.
                                </p>
                              </div>
                            )}

                            <div className="p-4 border-b border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <FaFileAlt className="text-xl text-blue-500" />
                                  <div>
                                    <h3 className="text-lg font-semibold text-gray-800">
                                      {order.orderId}
                                    </h3>
                                    <p className="text-xs text-gray-500">Date: {order.date}</p>
                                  </div>
                                </div>
                                {order.isUrgent && (
                                  <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-600 rounded-full">
                                    URGENT
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="p-4 space-y-3">
                              <div className="flex justify-between">
                                <p className="text-sm text-gray-700">
                                  College:{" "}
                                  <span className="font-medium">
                                    {order.items[0]?.selectedCollege}
                                  </span>
                                </p>
                              </div>
                              <div className="text-sm text-gray-700">
                                Name:{" "}
                                <span className="font-medium">{order.name || "Not provided"}</span>
                              </div>
                              <div className="text-sm text-gray-700">
                                Phone:{" "}
                                <span className="font-medium">{order.phone || "Not provided"}</span>
                              </div>
                            </div>

                            <div className="p-4 bg-gray-50 border-t border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex  mr-1 items-center space-x-2">
                                  {order.status === "Paid" ? (
                                    <FaCheckCircle className="text-green-500 text-lg" />
                                  ) : (
                                    <FaTimesCircle className="text-red-500 text-lg" />
                                  )}
                                  <span className="text-sm font-medium  text-gray-700">
                                    {order.status}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {order.notified ? (
                                    <button className="text-green-500 px-1 hover:text-green-600 text-sm flex items-center space-x-1 pointer-events-none">
                                      <span>Notified✅</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handlePrintClick(order.orderId)}
                                      className={`text-blue-500 hover:text-blue-600 text-sm flex items-center space-x-1 ${
                                        isRestricted && "opacity-50 pointer-events-none"
                                      }`}
                                    >
                                      <span>Notify</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handlePrintedStatusClick(order.orderId)}
                                    className={`text-blue-500 hover:text-blue-600 text-sm flex items-center space-x-1 ${
                                      isRestricted && "opacity-50 pointer-events-none"
                                    }`}
                                  >
                                    <span>Handover</span>
                                  </button>
                                  <button
                                    onClick={() => handleViewFiles(order)}
                                    className={`text-blue-500 hover:text-blue-600 text-sm flex items-center space-x-1 ${
                                      isRestricted && "opacity-30 pointer-events-none"
                                    }`}
                                  >
                                    <FaEye className="text-sm" />
                                    <span>View</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Divider and Notified Orders Section */}
                {notifiedOrders.length > 0 && (
                  <div className="my-8">
                    <hr className="border-t-2 border-gray-300" />
                    <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-4">
                      Notified Orders
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {notifiedOrders.map((order, index) => {
                        const elapsedSeconds = order.rawDate
                          ? (currentTime - order.rawDate) / 1000
                          : 0;
                        const remainingSeconds = order.rawDate
                          ? Math.max(600 - elapsedSeconds, 0)
                          : 0;
                        const isRestricted = !order.isUrgent && remainingSeconds > 0;
                        const formattedTime = formatTime(remainingSeconds);

                        return (
                          <div
                            key={index}
                            className="relative bg-white w-[fit-content] rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200 ease-in-out overflow-hidden"
                          >
                            {isRestricted && (
                              <div className="absolute inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex flex-col justify-center items-center text-center text-red-600 font-semibold text-sm z-10">
                                <p className="mb-2">
                                  Access restricted for{" "}
                                  <span className="font-bold">{formattedTime}</span>
                                </p>
                                <p className="text-xs text-gray-600">
                                  Please wait until the time expires.
                                </p>
                              </div>
                            )}

                            <div className="p-4 border-b border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <FaFileAlt className="text-xl text-blue-500" />
                                  <div>
                                    <h3 className="text-lg font-semibold text-gray-800">
                                      {order.orderId}
                                    </h3>
                                    <p className="text-xs text-gray-500">Date: {order.date}</p>
                                  </div>
                                </div>
                                {order.isUrgent && (
                                  <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-600 rounded-full">
                                    URGENT
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="p-4 space-y-3">
                              <div className="flex justify-between">
                                <p className="text-sm text-gray-700">
                                  College:{" "}
                                  <span className="font-medium">
                                    {order.items[0]?.selectedCollege}
                                  </span>
                                </p>
                                <p className="text-sm text-gray-700">
                                  Amount: <span className="font-medium">₹{order.totalAmount}</span>
                                </p>
                              </div>
                              <div className="text-sm text-gray-700">
                                Name:{" "}
                                <span className="font-medium">{order.name || "Not provided"}</span>
                              </div>
                              <div className="text-sm text-gray-700">
                                Phone:{" "}
                                <span className="font-medium">{order.phone || "Not provided"}</span>
                              </div>
                            </div>

                            <div className="p-4 bg-gray-50 border-t border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {order.status === "Paid" ? (
                                    <FaCheckCircle className="text-green-500 text-lg" />
                                  ) : (
                                    <FaTimesCircle className="text-red-500 text-lg" />
                                  )}
                                  <span className="text-sm font-medium text-gray-700">
                                    {order.status}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {order.notified ? (
                                    <button className="text-green-500 px-1 hover:text-green-600 text-sm flex items-center space-x-1 pointer-events-none">
                                      <span>Notified✅</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handlePrintClick(order.orderId)}
                                      className={`text-blue-500 hover:text-blue-600 text-sm flex items-center space-x-1 ${
                                        isRestricted && "opacity-50 pointer-events-none"
                                      }`}
                                    >
                                      <span>Notify</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handlePrintedStatusClick(order.orderId)}
                                    className={`text-blue-500 hover:text-blue-600 text-sm flex items-center space-x-1 ${
                                      isRestricted && "opacity-50 pointer-events-none"
                                    }`}
                                  >
                                    <span>Handover</span>
                                  </button>
                                  <button
                                    onClick={() => handleViewFiles(order)}
                                    className={`text-blue-500 hover:text-blue-600 text-sm flex items-center space-x-1 ${
                                      isRestricted && "opacity-30 pointer-events-none"
                                    }`}
                                  >
                                    <FaEye className="text-sm" />
                                    <span>View</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <PriceModal
        isOpen={isPriceModalOpen}
        onClose={() => setIsPriceModalOpen(false)}
        priceType={priceType}
        updatedPrices={updatedPrices}
        handleColorTypeChange={handleColorTypeChange}
        handleCombinationPriceChange={handleCombinationPriceChange}
        updatePrices={updatePrices}
      />
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        scheduleSettings={scheduleSettings}
        setScheduleSettings={setScheduleSettings}
        saveScheduleSettings={saveScheduleSettings}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
      />
      <BindingModal
        isOpen={isBindingModalOpen}
        onClose={() => setIsBindingModalOpen(false)}
        bindingPrices={bindingPrices}
        setBindingPrices={setBindingPrices}
        updateBindingPrices={updateBindingPrices}
      />
      <PrintConfirmationModal
        isOpen={isPrintConfirmationModalOpen}
        onClose={() => setIsPrintConfirmationModalOpen(false)}
        confirmPrint={confirmPrint}
        isProcessing={isProcessing}
      />
      <PrintedModal
        isOpen={isPrintedModalOpen}
        onClose={closePrintedModal}
        isProcessing={isProcessing}
      />
      <DiscountModal
        isOpen={isDiscountModalOpen}
        onClose={() => setIsDiscountModalOpen(false)}
        discountSettings={discountSettings}
        handleDiscountChange={handleDiscountChange}
        updateDiscountSettings={updateDiscountSettings}
      />
      <ImageCostModal
        isOpen={isImageCostModalOpen}
        onClose={() => setIsImageCostModalOpen(false)}
        imageCostSettings={imageCostSettings}
        setImageCostSettings={setImageCostSettings}
        handleUpdateImageCost={handleUpdateImageCost}
      />
      <FileViewerModal
        isOpen={isFileViewerModalOpen}
        onClose={() => setIsFileViewerModalOpen(false)}
        selectedFile={selectedFile}
        getFileViewerURL={getFileViewerURL}
      />
      <ViewFilesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentOrderFiles={currentOrderFiles}
        currentOrderId={currentOrderId}
        expandedGroupIndex={expandedGroupIndex}
        toggleGroupDetails={toggleGroupDetails}
        handleViewFile={handleViewFile}
        handlePrint={handlePrint}
      />

      <AdminOrderHistoryModal
        isOpen={isOrderHistoryOpen}
        onClose={() => setIsOrderHistoryOpen(false)}
      />
    </div>
  );
};

export default AdminDashboard;
